import { useCampaignNumApnsBeingFarmed } from 'data/campaign';
import { Field, Form, Formik } from 'formik';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import { useSWRConfig } from 'swr';
import { EAgentLandingPageType } from 'types/agent-landing-page';
import { HIGH_PROPENSITY_TO_SELL_PROPERTY_FILTER_PRESET } from 'types/campaign';
import * as Yup from 'yup';

import { ButtonBase } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import DeleteRoundedIcon from '@material-ui/icons/DeleteRounded';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import LibraryAddCheckRoundedIcon from '@material-ui/icons/LibraryAddCheckRounded';
import ScheduleRoundedIcon from '@material-ui/icons/ScheduleRounded';

import Button from 'creative-components/CustomButtons/Button';
import CustomInputDashboard from 'creative-components/CustomInput/CustomInputDashboard';

import ActionModal from 'components/ActionModal/ActionModal';
import AddOnPropertiesInput from 'components/AddOnPropertiesInput/AddOnPropertiesInput';
import { addressYupValidation, initialAddress } from 'components/AddressAutocomplete/AddressForm';
import { useAuthDataContext } from 'components/AuthDataProvider/AuthDataProvider';
import CampaignTargetingSection from 'components/CampaignTargetingSection/CampaignTargetingSection';
import ChooseAgentLandingPageType from 'components/ChooseAgentLandingPageType/ChooseAgentLandingPageType';
import ChooseTouchTemplateDesign from 'components/ChooseTouchTemplateDesign/ChooseTouchTemplateDesign';
import DashboardSection from 'components/DashboardSection/DashboardSection';
import { useDataFilterContext } from 'components/DataMap/DataFilterProvider';
import ExtraMailOutAddressesInput from 'components/ExtraMailOutAddressesInput/ExtraMailOutAddressesInput';
import FormikOnError from 'components/FormikOnError/FormikOnError';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import { useLoadingIndicatorContext } from 'components/LoadingIndicator/LoadingIndicatorProvider';
import { useUserInfoContext } from 'components/UserInfoProvider/UserInfoProvider';

import analyticsClient from 'utils/analytics';
import { saveCampaignDraft } from 'utils/api';
import {
  nameRegExp,
  numberWithCommas,
  phoneRegExp,
  showAPIErrorAlert,
} from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 40,
  },
  container: {
    margin: '20px 0 40px',
  },
  settingInput: {
    maxWidth: '400px',
  },
  spacerSmall: {
    marginTop: '70px',
  },
  spacerBig: {
    marginTop: '140px',
  },
  deleteDraftButton: {
    marginRight: '20px',
  },
  nextButton: {
    float: 'right',
  },
  confirmDisclaimerButton: {
    backgroundColor: `${theme.palette.primary.main} !important`,
  },
}));

const addOnPropertiesCountName = 'Add On Properties';

const CreateCampaignDataSelection = ({
  draftCampaign,
  setDraftCampaign,
  confirmDraftDeletion,
  setStep,
  isAnnualBilling,
  handleSetAnnualBilling,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const { mutate } = useSWRConfig();
  const { userInfo } = useUserInfoContext();
  const { user } = useAuthDataContext();
  const { showLoadingIndicatorModal, hideLoadingIndicatorModal } = useLoadingIndicatorContext();
  const { selectedEventType } = useDataFilterContext();

  const { numApnsBeingFarmed } = useCampaignNumApnsBeingFarmed();

  const targetingRef = React.useRef(null);

  // For Formik extraMailOutAddresses so we can access its values from the outside
  const [extraMailOutsCount, setExtraMailOutsCount] = React.useState(draftCampaign?.extraMailOutRecipients.length ?? 0);
  const [targetingState, setTargetingState] = React.useState({
    apnAvailableCounts: [],
    apnSelectedCounts: [],
    allApnAvailableCounts: [],
    highPropensityApnAvailableCounts: [],
    apnsTotalInAreaCount: 0,
    apnsTotalSelectedCount: 0,
    farmAreaAllAvailableCount: 0,
    farmAreaHighPropensityAvailableCount: 0,
    hasFarmLimit: false,
    showHighPropensityTargeting: false,
    showOverlapWarning: false,
    useHighPropensityToSell: draftCampaign?.propertyFilterPreset === HIGH_PROPENSITY_TO_SELL_PROPERTY_FILTER_PRESET,
  });
  const [isSavingAsDraft, setIsSavingAsDraft] = React.useState(undefined);

  const [showFarmAreaDisclaimer, setShowFarmAreaDisclaimer] = React.useState(false);

  if (!selectedEventType) return (<LoadingIndicator />);

  const initSelectedZipcodes = React.useMemo(() => (
    draftCampaign?.apnSelectedCounts
      ?.filter(({ name }) => name !== 'Custom Shapes' && name !== addOnPropertiesCountName)
      .map(({ name }) => name) ?? []
  ), [draftCampaign]);

  return (
    <div className={classes.root}>
      <Formik
        initialValues={{
          extraMailOutAddresses: draftCampaign?.extraMailOutRecipients ?? [],
          firstName: '',
          lastName: '',
          email: '',
          phoneNumber: '',
          address: { ...initialAddress },
          farmAreaName: draftCampaign?.farmAreaName || '',
          agentLandingPageType: draftCampaign?.agentLandingPageType || EAgentLandingPageType.ComparableProperties,
          addOnPropertiesJsonString: draftCampaign?.addOnPropertiesJsonString || null,
          touchTemplateDesignId: draftCampaign?.touchTemplateDesign || null,
        }}
        onSubmit={async (values) => {
          const {
            extraMailOutAddresses, farmAreaName, agentLandingPageType, addOnPropertiesJsonString, touchTemplateDesignId,
          } = values;
          showLoadingIndicatorModal();

          try {
            const { campaign } = await saveCampaignDraft({
              campaignId: draftCampaign?._id ?? undefined,
              extraMailOutAddresses,
              farmAreaName,
              agentLandingPageType: selectedEventType.name === 'Commercial' ? EAgentLandingPageType.ContactForm : agentLandingPageType, // Restrict agent landing page type for commercial properties
              addOnPropertiesJsonString,
              touchTemplateDesignId,
              isAnnualBilling,

              // Farm selection data for non-auto-renewal campaigns
              ...(!draftCampaign || !draftCampaign.autoRenewalForCampaign ? {
                eventTypeId: !draftCampaign ? selectedEventType._id : undefined, // Only send for new campaigns
                ...targetingRef.current.getSubmissionData(),
              } : {}),
            });

            // Track GTM event
            await analyticsClient.track('campaign_creation_completed', {
              agentdata_user_id: user.id,
              role: user.role,
              brokerage_id: userInfo.agentInfo.brokerage?._id ?? null,
              campaign_event_type: selectedEventType.name,
            });

            if (isSavingAsDraft) {
              if (draftCampaign) {
                // Mark as stale so cache updates next time
                mutate(`/campaigns/${campaign._id}`);
              }

              // Return to campaigns
              history.push('/dashboard/campaigns');

              toast.success('Your campaign draft has been saved. You can return to this campaign to pick up where you left off in the future.');
            } else {
              setDraftCampaign(campaign);

              // Go to the next page for editing touches
              setStep(2);
            }
          } catch (err) {
            console.error('error', err);
            showAPIErrorAlert(err);
          }

          hideLoadingIndicatorModal();
        }}
        validationSchema={Yup.object().shape({
          extraMailOutAddresses: Yup.array().of(Yup.object().shape({
            firstName: Yup.string().required().matches(nameRegExp),
            lastName: Yup.string().required().matches(nameRegExp),
            email: Yup.string().email().optional(),
            phoneNumber: Yup.string().matches(phoneRegExp, 'Phone number is not valid').optional(),
            address: addressYupValidation.required(),
          })),
          firstName: Yup.string().matches(nameRegExp).optional(),
          lastName: Yup.string().matches(nameRegExp).optional(),
          email: Yup.string().email().optional(),
          phoneNumber: Yup.string().matches(phoneRegExp, 'Phone number is not valid').optional(),
          address: addressYupValidation.optional(),
          farmAreaName: Yup.string().required(),
          agentLandingPageType: Yup.string().required(),
          addOnPropertiesJsonString: Yup.string().nullable(),
          touchTemplateDesignId: Yup.string().required(),
        })}
      >
        {({
          errors, handleSubmit, isSubmitting, values,
        }) => {
          const handleOnSave = (isNext) => {
            const {
              firstName, lastName, email, phoneNumber, address,
            } = values;

            if (!draftCampaign || !draftCampaign.autoRenewalForCampaign) {
              // Make sure they refetched since last change
              if (!targetingRef.current.areDataCountsUpToDate()) {
                toast.warning('Please click on the Generate Costs button to update the number of APNs in your latest farm area selection!');
                return;
              }

              // Make sure they have > 0 selected parcels
              if (targetingState.apnsTotalSelectedCount === 0) {
                toast.warning('There are no parcels in your selection area. Please select an area or expand your selection!');
                return;
              }
            }

            if (firstName || lastName || email || phoneNumber || address.address1) {
              toast.warning('Did you forget to add an extra recipient to your campaign?');
              return;
            }

            if (!isNext) {
              setIsSavingAsDraft(true);
              handleSubmit();
              return;
            }

            // Don't show the farm area disclaimer for auto-renewal campaigns, high propensity
            // campaigns, or if there is no difference in the available and total count.
            // HP2S only has an omitted-records count, so we do not have a comparable total-in-area
            // count for that segment.
            if (draftCampaign?.autoRenewalForCampaign || targetingState.useHighPropensityToSell || targetingState.apnsTotalSelectedCount === targetingState.apnsTotalInAreaCount) {
              setIsSavingAsDraft(false);
              handleSubmit();
            } else {
              setShowFarmAreaDisclaimer(true);
            }
          };

          return (
            <Form>
              <FormikOnError>
                {showFarmAreaDisclaimer && (
                  <ActionModal
                    maxWidth="sm"
                    backgroundColor={theme.palette.offWhite.main}
                    textColor={theme.palette.darkGray.main}
                    icon={<LibraryAddCheckRoundedIcon />}
                    onClose={() => setShowFarmAreaDisclaimer(false)}
                    title="Farm Area Disclaimer"
                    message={(
                      <div>
                        {`I agree that my campaign will only be sent to ${numberWithCommas(targetingState.apnsTotalSelectedCount)} of the ${numberWithCommas(targetingState.apnsTotalInAreaCount)} APNs in the target Farm Area.`}

                        {targetingState.hasFarmLimit ? ` I set the limit of ${numberWithCommas(targetingState.apnSelectedCounts.reduce((sum, { count }) => sum + count, 0))} in the Farm Area selector above.` : ''}
                        <br />
                        <br />
                        Of the APNs in the target farm area, which the campaign is NOT targeting, some of them may be used exclusively by other agents.
                      </div>
                    )}
                    buttons={[
                      <ButtonBase onClick={() => setShowFarmAreaDisclaimer(false)}>Go Back</ButtonBase>,
                      <ButtonBase
                        onClick={() => {
                          setShowFarmAreaDisclaimer(false);
                          setIsSavingAsDraft(false);
                          handleSubmit();
                        }}
                        className={classes.confirmDisclaimerButton}
                      >
                        Continue
                      </ButtonBase>,
                    ]}
                  />
                )}

                <CampaignTargetingSection
                  ref={targetingRef}
                  selectedEventType={selectedEventType}
                  customMonthlyRatePerApn={userInfo?.agentInfo.customMonthlyRatePerApn}
                  isAnnualBilling={isAnnualBilling}
                  setIsAnnualBilling={handleSetAnnualBilling}
                  extraMailOutsCount={extraMailOutsCount}
                  numApnsAlreadyBeingFarmed={numApnsBeingFarmed}
                  renewalPreviousMonthlyRatePerApn={draftCampaign?.autoRenewalForCampaign?.costs.monthlyRatePerApn ?? undefined}
                  estimatedNumberOfApnsOverride={draftCampaign?.autoRenewalForCampaign ? draftCampaign.estimatedNumberOfApns : undefined}
                  initPropertyFilters={draftCampaign?.propertyFilters ?? null}
                  initSelectionPolygonPaths={draftCampaign?.selectionPolygons.coordinates ?? []}
                  initSelectedZipcodes={initSelectedZipcodes}
                  preferredUseHighPropensityToSell={draftCampaign?.propertyFilterPreset === HIGH_PROPENSITY_TO_SELL_PROPERTY_FILTER_PRESET ? true : null}
                  onStateChange={setTargetingState}
                  hideMap={!!draftCampaign?.autoRenewalForCampaign}
                >
                  {({
                    apnAvailableCounts,
                    setApnAvailableCounts,
                    allApnAvailableCounts,
                    setAllApnAvailableCounts,
                    highPropensityApnAvailableCounts,
                    setHighPropensityApnAvailableCounts,
                    apnSelectedCounts,
                    setApnSelectedCounts,
                    setApnsTotalSelectedCount,
                    apnsTotalInAreaCount,
                    setApnsTotalInAreaCount,
                  }) => (
                    <>
                      <div className={classes.spacerSmall} />

                      <DashboardSection sectionName="Add Extra APNs & Recipients" />
                      <div className={classes.container}>
                        {!draftCampaign?.autoRenewalForCampaign && (
                          <>
                            <div className={classes.container}>
                              <AddOnPropertiesInput
                                apnAvailableCounts={apnAvailableCounts}
                                setApnAvailableCounts={setApnAvailableCounts}
                                allApnAvailableCounts={allApnAvailableCounts}
                                setAllApnAvailableCounts={setAllApnAvailableCounts}
                                highPropensityApnAvailableCounts={highPropensityApnAvailableCounts}
                                setHighPropensityApnAvailableCounts={setHighPropensityApnAvailableCounts}
                                apnSelectedCounts={apnSelectedCounts}
                                setApnSelectedCounts={setApnSelectedCounts}
                                setApnsTotalSelectedCount={setApnsTotalSelectedCount}
                                apnsTotalInAreaCount={apnsTotalInAreaCount}
                                setApnsTotalInAreaCount={setApnsTotalInAreaCount}
                              />
                            </div>

                            <div className={classes.spacerBig} />
                          </>
                        )}

                        <ExtraMailOutAddressesInput setExtraMailOutsCount={setExtraMailOutsCount} />
                      </div>
                    </>
                  )}
                </CampaignTargetingSection>

                {/* NOTE: We took out DocuSign 06/21/2022 */}
                {/* <DashboardBubble
                  rootClass={classes.campaignDatesMessage}
                  backgroundColor={`rgb(${hexToRgb(theme.palette.warning.main)}, 0.1)`}
                  icon={<WarningRoundedIcon htmlColor={theme.palette.warning.main} />}
                  content="The campaign start and end dates may shift to a later date depending on when the DocuSign contract is completed by the agent."
                /> */}

                <div className={classes.spacerBig} />

                <DashboardSection sectionName="Campaign Settings" />
                <div className={classes.container}>
                  <Field name="farmAreaName">
                    {({ field }) => (
                      <CustomInputDashboard
                        labelText="Farm Area Name"
                        inputProps={{
                          ...field,
                          placeholder: 'Enter name',
                          endAdornment: <InfoOutlinedIcon />,
                          className: classes.settingInput,
                        }}
                        error={errors[field.name] !== undefined}
                        tooltip="This will be your farm area name merged onto certain marketing materials and refers to you being an expert in that community, neighborhood, zipcode and/or city, i.e. Hollywood Hills, The Westside, Crescent Heights"
                      />
                    )}
                  </Field>
                  {/* Only show the agent landing page selector for non-commercial properties since we don't do comparables for commercial properties */}
                  {selectedEventType.name !== 'Commercial' && (
                    <div style={{ marginTop: '40px' }}>
                      <ChooseAgentLandingPageType />
                    </div>
                  )}
                  <div style={{ marginTop: '40px' }}>
                    <ChooseTouchTemplateDesign eventType={selectedEventType} />
                  </div>
                </div>

                <div className={classes.spacerBig} />

                {draftCampaign !== null && (
                  <Button
                    round
                    color="danger"
                    onClick={confirmDraftDeletion}
                    className={classes.deleteDraftButton}
                  >
                    <DeleteRoundedIcon />
                    {draftCampaign.autoRenewalForCampaign ? 'Cancel Renewal' : 'Delete Draft'}
                  </Button>
                )}

                <Button
                  round
                  color="dark"
                  disabled={isSubmitting}
                  onClick={() => handleOnSave(false)}
                >
                  <ScheduleRoundedIcon />
                  Save Draft
                </Button>

                <Button
                  round
                  color="primary"
                  className={classes.nextButton}
                  disabled={isSubmitting}
                  onClick={() => handleOnSave(true)}
                >
                  Next
                </Button>
              </FormikOnError>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default CreateCampaignDataSelection;
